import React from 'react'
import { Flex } from 'rebass'
import { Mutation, Query } from 'react-apollo'
import { gql } from 'apollo-boost'
import { Field, Formik } from 'formik'
import * as yup from 'yup'

import TextField from '../TextField'
import Text from '../Text'
import Button from '../Button'
import Loading from '../Loading'
import RoleSelect from '../RoleSelect'
import Form, { Group, Heading } from '../Form'
import { yupPhone, yupZip } from '../../utils/validations'
import StateSelect from '../StateSelect'
import Checkbox from '../Checkbox'
import Badge from '../Badge'

import { convertRole } from '../../utils/strings'

const GET_USER = gql`
  query user($id: ID!) {
    user(id: $id) {
      id
      emailAddress
      firstName
      middleName
      lastName
      phoneNumber
      addresses(where: {type: PRIMARY}) {
        id
        type
        line1
        line2
        city        
        stateOrProvince {
          id
        }
        postalCode
      }
      authId
      role
      comments
      company
    }
  }
`

const UPDATE_USER = gql`
  mutation updateUser(
  $id: ID!
  $emailAddress: String!
  $firstName: String!
  $middleName: String
  $lastName: String!
  $phoneNumber: String
  $company: String
  $address: ID!
  $line1: String!
  $line2: String
  $city: String!
  $stateOrProvince: ID!
  $postalCode: String!
  $createLogin: Boolean!
  $password: String!
  $role: Role!
  $comments: String
  ) {
    updateUser(
      id: $id
      emailAddress: $emailAddress
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      phoneNumber: $phoneNumber
      company: $company
      address: $address
      line1: $line1
      line2: $line2
      city: $city
      stateOrProvince: $stateOrProvince
      postalCode: $postalCode
      createLogin: $createLogin
      password: $password
      role: $role
      comments: $comments
    ) {
      id
    }
  }
`
yup.addMethod(yup.string, 'phone', yupPhone)
yup.addMethod(yup.string, 'zip', yupZip)

const validationSchema = yup.object().shape({
  id: yup.string().required('ID is required'),
  emailAddress: yup.string().required('Email address is required').email('Email address is not valid'),
  firstName: yup.string().required('First name is required'),
  middleName: yup.string(),
  lastName: yup.string().required('Last name is required'),
  phoneNumber: yup.string().phone(),
  comments: yup.string(),
  company: yup.string(),
  line1: yup.string().required('Address is required'),
  line2: yup.string(),
  city: yup.string().required('City is required'),
  stateOrProvince: yup.string().required('State is required'),
  postalCode: yup.string().required('Zip code is required').zip(),
  createLogin: yup.boolean(),
  password: yup.string().when('createLogin', {is: true, then: yup.string().required('Password is required').min(6, 'Password too short')}),
  role: yup.mixed().oneOf(['ADMIN', 'SALES', 'CLIENT']).required('Role is required')
})

const initialValues = user => ({
  id: user.id || '',
  emailAddress: user.emailAddress || '',
  firstName: user.firstName || '',
  middleName: user.middleName || '',
  lastName: user.lastName || '',
  phoneNumber: user.phoneNumber || '',
  company: user.company || '',
  address: (user.addresses[0] && user.addresses[0].id) || '',
  line1: (user.addresses[0] && user.addresses[0].line1) || '',
  line2: (user.addresses[0] && user.addresses[0].line2) || '',
  city: (user.addresses[0] && user.addresses[0].city) || '',
  stateOrProvince: (user.addresses[0] && user.addresses[0].stateOrProvince.id) || '',
  postalCode: (user.addresses[0] && user.addresses[0].postalCode) || '',
  createLogin: false, // might not be displayed
  password: '', // might not be displayed
  role: user.role || '',
  comments: user.comments || ''
})

const onSubmit = (values, {setSubmitting}, createUser) => {
  createUser({variables: values})
  setSubmitting(false)
}

const Update = ({id, onCompleted, onError}) =>
  <Query
    query={GET_USER}
    variables={{id}}
    fetchPolicy='cache-and-network'>
    {({loading, error, data}) => {
      if (loading) return <Loading />
      if (error) return <Text error>{error.message}</Text>
      if (!data.user) return <Text>Nothing was found!</Text>

      return (
        <Mutation
          mutation={UPDATE_USER}
          onCompleted={onCompleted}
          onError={onError}>
          {(updateUser, {error, loading}) =>
            <Formik
              initialValues={initialValues(data.user)}
              validationSchema={validationSchema}
              onSubmit={(values, actions) => onSubmit(values, actions, updateUser)}>
              {({isSubmitting, values}) =>
                <Form>

                  <Flex justifyContent='space-between' flexDirection={['column', 'row']} pb={2}>
                    <Flex flexDirection='column'>
                      <Text h2 secondary>{`${values.firstName} ${values.lastName}`}</Text>
                      <Text fontSize={3} pb={2} ml='2px' light>{values.emailAddress}</Text>
                    </Flex>
                    <Badge secondary alignSelf='flex-start'>{convertRole(values.role)}</Badge>
                  </Flex>

                  <Heading>General</Heading>
                  <Group>
                    <Field component={TextField} name='firstName' required />
                    <Field component={TextField} name='middleName' />
                    <Field component={TextField} name='lastName' required />
                  </Group>
                  <Group>
                    <Field component={TextField} name='phoneNumber' phone />
                    <Field component={TextField} name='company' />
                  </Group>

                  <Heading>Location</Heading>
                  <Group>
                    <Field component={TextField} name='line1' label='Address 1' required />
                    <Field component={TextField} name='line2' label='Address 2' />
                  </Group>
                  <Group>
                    <Field component={TextField} name='city' required />
                    <Field component={StateSelect} name='stateOrProvince' label='State' required />
                    <Field component={TextField} name='postalCode' label='Zip Code' required />
                  </Group>

                  <Heading>Purpose</Heading>
                  {!data.user.authId && <Field component={Checkbox} name='createLogin' label='Do they need to login?' />}
                  <Group>
                    {!data.user.authId
                    && <Field component={TextField} name='password' required={values.createLogin} disabled={!values.createLogin} value={values.createLogin ? values.password : ''} hideError={!values.createLogin} />
                    }
                    <Field component={RoleSelect} name='role' required width={[1, data.user.authId ? 1 / 3 : 1]} />
                  </Group>

                  <Heading>Help</Heading>
                  <Field component={TextField} name='comments' label='Comments' textArea rows={5} />

                  {error && <Text error center>{error.message}</Text>}
                  <Button type='submit' fluid disabled={loading || isSubmitting}>Save</Button>
                </Form>
              }
            </Formik>
          }
        </Mutation>
      )
    }}
  </Query>

export default Update


// WEBPACK FOOTER //
// ./src/components/Users/Update.js